/**
 * Per-column visual weight for report tables.
 *
 * Deliberately frontend-only rather than a field on the stored `columnSchema`: a styling
 * tweak then ships atomically with the code it belongs to, instead of needing a hand-run
 * SQL update in every environment before the change is visible.
 *
 * Keyed by report name, because it is the only key that is stable across environments —
 * `reportId` is generated per environment and `staticReportKey` is null for the
 * AI-authored reports. Renaming a report in the database therefore drops its styling,
 * which is the accepted trade for not committing per-environment GUIDs here.
 */
export type ColumnEmphasis = 'strong' | 'muted';

const RULES: Record<string, Record<string, ColumnEmphasis>> = {
    'Company Balance Summary': {
        // Balance is the figure the report exists to answer; the Unreported columns are
        // supporting detail that should not compete with it.
        Balance: 'strong',
        'Unreported Withdrawals': 'muted',
        'Unreported Total': 'muted'
    }
};

/**
 * Collapses a name to a comparison token. Separators are dropped, not just trimmed, so a
 * rule written as the display label ("Unreported Withdrawals") also matches the SQL alias
 * behind it ("UnreportedWithdrawals") — without this the key fallback below would only
 * ever fire for single-word columns.
 */
const key = (value: string) => value.replace(/[^a-z0-9]/gi, '').toLowerCase();

const normalise = (rules: Record<string, ColumnEmphasis>) =>
    Object.fromEntries(
        Object.entries(rules).map(([column, emphasis]) => [
            key(column),
            emphasis
        ])
    );

const NORMALISED: Record<
    string,
    Record<string, ColumnEmphasis>
> = Object.fromEntries(
    Object.entries(RULES).map(([report, columns]) => [
        key(report),
        normalise(columns)
    ])
);

/**
 * Resolves the emphasis for one column of one report. Columns are matched on their
 * display label first and their schema key second, so a rule keeps working whether it
 * was written against the header the user reads or the SQL alias behind it.
 *
 * Names and labels arrive from operator-authored database rows, so both sides are reduced
 * to a separator-free token before matching — stray whitespace in a report name should not
 * silently un-style the whole report.
 */
export const getColumnEmphasis = (
    reportName: string | undefined,
    columnLabel: string | undefined,
    columnKey?: string
): ColumnEmphasis | undefined => {
    if (!reportName) return undefined;
    const columns = NORMALISED[key(reportName)];
    if (!columns) return undefined;
    return (
        (columnLabel ? columns[key(columnLabel)] : undefined) ??
        (columnKey ? columns[key(columnKey)] : undefined)
    );
};
